import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { ShoppingBag, Zap, ArrowRight, Flame, Lock, ChevronLeft, ChevronRight, ShoppingCart, Search, Phone, Mail, MapPin, Calendar, ExternalLink, CalendarCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveImg(url) {
  if (!url) return "";
  return url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

// Gallery Carousel with center-enlarged effect
function GalleryCarousel({ images, speed }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  const total = images.length;

  const next = useCallback(() => setCurrentIndex(i => (i + 1) % total), [total]);
  const prev = useCallback(() => setCurrentIndex(i => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (total <= 1) return;
    const ms = Math.max(500, (110 - (speed || 30)) * 80);
    intervalRef.current = setInterval(next, ms);
    return () => clearInterval(intervalRef.current);
  }, [total, speed, next]);

  const pause = () => clearInterval(intervalRef.current);
  const resume = () => {
    if (total <= 1) return;
    const ms = Math.max(500, (110 - (speed || 30)) * 80);
    intervalRef.current = setInterval(next, ms);
  };

  if (total === 0) return null;

  const getOffset = (i) => {
    let diff = i - currentIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;
    return diff;
  };

  const visibleIndices = [];
  for (let i = 0; i < total; i++) {
    const off = getOffset(i);
    if (Math.abs(off) <= 3) visibleIndices.push(i);
  }

  return (
    <div className="relative overflow-hidden" onMouseEnter={pause} onMouseLeave={resume} data-testid="gallery-carousel">
      <div className="flex items-center justify-center h-72 md:h-96 relative">
        {visibleIndices.map(i => {
          const off = getOffset(i);
          const absOff = Math.abs(off);
          const scale = absOff === 0 ? 1 : absOff === 1 ? 0.7 : 0.5;
          const opacity = absOff === 0 ? 1 : absOff === 1 ? 0.7 : 0.4;
          const zIndex = 10 - absOff;
          const translateX = off * 220;
          return (
            <div
              key={i}
              className="absolute transition-all duration-500 ease-out"
              style={{
                transform: `translateX(${translateX}px) scale(${scale})`,
                opacity,
                zIndex,
              }}
            >
              <div className={`border-2 border-black bg-white shadow-[${absOff === 0 ? "8px_8px" : "4px_4px"}_0px_0px_rgba(0,0,0,1)] overflow-hidden transition-all duration-500`}>
                <img
                  src={`${API}/files/${images[i].storage_path}`}
                  alt={images[i].name || "Completed work"}
                  className={`object-contain ${absOff === 0 ? "w-64 h-64 md:w-80 md:h-80" : "w-44 h-44 md:w-56 md:h-56"}`}
                  draggable={false}
                />
              </div>
            </div>
          );
        })}
      </div>
      {/* Controls */}
      {total > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button onClick={() => { prev(); pause(); }} className="w-10 h-10 border-2 border-black bg-white flex items-center justify-center hover:bg-zinc-100 transition-colors shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" data-testid="gallery-prev">
            <ChevronLeft size={20} strokeWidth={2.5} />
          </button>
          <div className="flex gap-1.5">
            {images.map((_, i) => (
              <button key={i} onClick={() => setCurrentIndex(i)} className={`w-2.5 h-2.5 border border-black transition-colors ${i === currentIndex ? "bg-[#FF3366]" : "bg-zinc-200"}`} />
            ))}
          </div>
          <button onClick={() => { next(); pause(); }} className="w-10 h-10 border-2 border-black bg-white flex items-center justify-center hover:bg-zinc-100 transition-colors shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" data-testid="gallery-next">
            <ChevronRight size={20} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, index, onClick }) {
  const [imgIdx, setImgIdx] = useState(0);
  const images = product.all_images || (product.image_url ? [product.image_url] : []);
  const total = images.length;

  const resolveUrl = (url) => url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

  const prevImg = (e) => { e.stopPropagation(); setImgIdx(i => (i - 1 + total) % total); };
  const nextImg = (e) => { e.stopPropagation(); setImgIdx(i => (i + 1) % total); };

  return (
    <div
      className="brutal-card cursor-pointer animate-slide-up group"
      style={{ animationDelay: `${index * 0.1}s` }}
      onClick={onClick}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative h-52 overflow-hidden border-b-2 border-black bg-zinc-100">
        {images.length > 0 ? (
          <img
            src={resolveUrl(images[imgIdx])}
            alt={product.name}
            className="w-full h-full object-contain p-2 transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Flame size={48} className="text-zinc-300" strokeWidth={2.5} />
          </div>
        )}
        {/* Image scroll buttons */}
        {total > 1 && (
          <>
            <button onClick={prevImg}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 border-2 border-black bg-white/90 flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:bg-white z-10 transition-colors"
              data-testid={`product-img-prev-${product.id}`}>
              <ChevronLeft size={14} strokeWidth={2.5} />
            </button>
            <button onClick={nextImg}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 border-2 border-black bg-white/90 flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:bg-white z-10 transition-colors"
              data-testid={`product-img-next-${product.id}`}>
              <ChevronRight size={14} strokeWidth={2.5} />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
              {images.map((_, i) => (
                <button key={i} onClick={(e) => { e.stopPropagation(); setImgIdx(i); }}
                  className={`w-2 h-2 border border-black transition-colors ${i === imgIdx ? "bg-[#FF3366]" : "bg-white/80"}`}
                  data-testid={`product-img-dot-${product.id}-${i}`} />
              ))}
            </div>
          </>
        )}
        {product.category && (
          <Badge className="absolute top-3 left-3 bg-[#00E5FF] text-black border-2 border-black rounded-none font-mono text-[10px] tracking-[0.15em] uppercase">
            {product.category}
          </Badge>
        )}
        {(product.quantity || 0) <= 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-red-500 text-white border-2 border-black px-4 py-2 font-heading font-bold text-sm uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">Out of Stock</span>
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="font-heading text-lg font-bold uppercase mb-2">{product.name}</h3>
        {product.description && (
          <p className="text-sm text-zinc-500 mb-4 line-clamp-2">{product.description}</p>
        )}
        <div className="flex items-center justify-between">
          <span className="font-heading text-2xl font-black text-[#FF3366]">
            ${product.price.toFixed(2)}
          </span>
          <span className="brutal-btn brutal-btn-secondary text-xs px-4 py-2 inline-flex items-center gap-2">
            Customize <ArrowRight size={14} strokeWidth={2.5} />
          </span>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [promoApplied, setPromoApplied] = useState(null);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminError, setAdminError] = useState("");
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [gallerySpeed, setGallerySpeed] = useState(30);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [cartCount, setCartCount] = useState(0);
  const [contactInfo, setContactInfo] = useState({});
  const [activeEvents, setActiveEvents] = useState([]);
  const [eventsOpen, setEventsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
    fetchGallery();
    fetchGalleryVideos();
    fetchContactInfo();
    fetchActiveEvents();
    updateCartCount();
    const promoDiscount = searchParams.get("promo_discount");
    const promoCode = searchParams.get("promo_code");
    const promoType = searchParams.get("promo_type");
    if (promoDiscount && promoCode) {
      setPromoApplied({ discount: promoDiscount, code: promoCode, type: promoType || "percentage" });
      sessionStorage.setItem("promo_code", promoCode);
      sessionStorage.setItem("promo_discount", promoDiscount);
      sessionStorage.setItem("promo_type", promoType || "percentage");
    }
  }, [searchParams]);

  const updateCartCount = () => {
    const items = JSON.parse(sessionStorage.getItem("cart_items") || "[]");
    setCartCount(items.length);
  };

  const fetchContactInfo = async () => {
    try { const res = await axios.get(`${API}/contact-info`); setContactInfo(res.data); } catch (e) {  }
  };

  const fetchActiveEvents = async () => {
    try { const res = await axios.get(`${API}/events/active`); setActiveEvents(res.data); } catch (e) {  }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      const active = res.data.filter(p => p.active);
      // Fetch product images for each product
      const withImages = await Promise.all(active.map(async (p) => {
        try {
          const imgRes = await axios.get(`${API}/products/${p.id}/images`);
          const allImgs = [];
          if (p.image_url) allImgs.push(p.image_url);
          imgRes.data.forEach(img => {
            const url = `/api/files/${img.storage_path}`;
            if (!allImgs.includes(url)) allImgs.push(url);
          });
          return { ...p, all_images: allImgs };
        } catch { return { ...p, all_images: p.image_url ? [p.image_url] : [] }; }
      }));
      setProducts(withImages);
    } catch (e) {
      
    } finally {
      setLoading(false);
    }
  };

  const fetchGallery = async () => {
    try {
      const [worksRes, settingsRes] = await Promise.all([
        axios.get(`${API}/completed-works`),
        axios.get(`${API}/settings`)
      ]);
      setGalleryImages(worksRes.data);
      setGallerySpeed(settingsRes.data.gallery_speed || 30);
    } catch (e) {  }
  };

  const fetchGalleryVideos = async () => {
    try {
      const res = await axios.get(`${API}/gallery-videos`);
      setGalleryVideos(res.data);
    } catch (e) {  }
  };

  const handleProductClick = (productId) => {
    navigate(`/customize/${productId}`);
  };

  const productCategories = [...new Set(products.map(p => p.category).filter(Boolean))];
  const filteredProducts = selectedCategory === "all" ? products : products.filter(p => p.category === selectedCategory);

  const handleAdminLogin = () => {
    if (adminPassword === "Select56") {
      sessionStorage.setItem("admin_authed", "true");
      setAdminDialogOpen(false);
      setAdminPassword("");
      setAdminError("");
      navigate("/admin");
    } else {
      setAdminError("Incorrect password");
    }
  };

  const handleAdminKeyDown = (e) => {
    if (e.key === "Enter") handleAdminLogin();
  };

  return (
    <div className="min-h-screen grain-overlay" data-testid="landing-page">
      {/* Copyright Warning Marquee */}
      <div className="warning-banner" data-testid="copyright-warning">
        <Marquee speed={40} gradient={false}>
          <span className="mx-8 text-sm tracking-widest">
            WARNING: No trademarked or copyrighted images can be processed &nbsp;&bull;&nbsp;
            WARNING: No trademarked or copyrighted images can be processed &nbsp;&bull;&nbsp;
            WARNING: No trademarked or copyrighted images can be processed &nbsp;&bull;&nbsp;
          </span>
        </Marquee>
      </div>

      {/* Promo Banner */}
      {promoApplied && (
        <div className="bg-[#00E5FF] border-b-2 border-black p-3 text-center" data-testid="promo-banner">
          <span className="font-heading font-bold text-black text-sm uppercase tracking-wider">
            Promo Applied! {promoApplied.discount}% OFF your order with code: {promoApplied.code}
          </span>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b-2 border-black bg-black" data-testid="hero-section">
        <div className="relative z-10 px-8 md:px-16 py-20 md:py-28 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div className="flex-1">
              <div className="inline-block bg-[#FFD600] border-2 border-black px-4 py-2 mb-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <span className="font-mono text-xs font-bold tracking-[0.2em] text-black uppercase">Live Events & Custom Orders</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase leading-none mb-6">
                Burn On Site<br />
                <span className="text-[#FF3366]">Laser Engraving</span>
              </h1>
              <p className="text-base md:text-lg text-zinc-300 max-w-xl mb-10 leading-relaxed">
                Custom laser engravings on-demand. Choose your product, upload your design or pick from our catalog, and we will burn it right before your eyes.
              </p>
              <a
                href="#products"
                className="brutal-btn inline-flex items-center gap-3 text-sm no-underline"
                data-testid="shop-now-btn"
              >
                <ShoppingBag size={18} strokeWidth={2.5} />
                Shop Products
                <ArrowRight size={18} strokeWidth={2.5} />
              </a>
            </div>
            <div className="flex-shrink-0">
              <img
                src="/logo.png"
                alt="Burn On Site Laser Engraving Logo"
                className="w-56 h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 object-contain invert drop-shadow-[0_0_30px_rgba(255,51,102,0.3)]"
                data-testid="hero-logo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Strip */}
      <div className="bg-black text-white border-b-2 border-black py-5">
        <div className="max-w-7xl mx-auto px-8 flex flex-wrap justify-center gap-8 md:gap-16">
          <div className="flex items-center gap-3">
            <Zap size={20} className="text-[#FFD600]" strokeWidth={2.5} />
            <span className="font-mono text-xs font-bold tracking-[0.15em] uppercase">Custom Designs</span>
          </div>
          <div className="flex items-center gap-3">
            <Flame size={20} className="text-[#FF3366]" strokeWidth={2.5} />
            <span className="font-mono text-xs font-bold tracking-[0.15em] uppercase">Live Engraving</span>
          </div>
          <div className="flex items-center gap-3">
            <ShoppingBag size={20} className="text-[#00E5FF]" strokeWidth={2.5} />
            <span className="font-mono text-xs font-bold tracking-[0.15em] uppercase">Event Ready</span>
          </div>
        </div>
      </div>

      {/* Events & Book Us Section */}
      <section className="py-16 md:py-20 border-b-2 border-black bg-white" data-testid="planned-events-section">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="text-center mb-10">
            <span className="font-mono text-xs font-bold tracking-[0.2em] text-[#FF3366] uppercase block mb-3">Where to Find Us</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase mb-4">Upcoming Events</h2>
            <p className="text-zinc-500 text-sm max-w-md mx-auto">Come see us in person! We'll be set up at these events with live laser engraving.</p>
          </div>
          {activeEvents.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {activeEvents.map(event => (
                <div key={event.id} className="brutal-card overflow-hidden group hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-shadow" data-testid={`planned-event-${event.id}`}>
                  <div className="bg-[#FF3366] text-white p-4 border-b-2 border-black">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar size={16} strokeWidth={2.5} />
                      <span className="font-heading font-bold text-sm uppercase">{event.name}</span>
                    </div>
                    {event.event_date && (
                      <span className="text-xs font-mono opacity-80">
                        {new Date(event.event_date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    )}
                  </div>
                  <div className="p-5 space-y-3">
                    {event.location && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin size={14} strokeWidth={2.5} className="text-[#FF3366] flex-shrink-0" />
                        <span>{event.location}</span>
                      </div>
                    )}
                    {event.link && (
                      <a href={event.link} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-[#FF3366] hover:text-[#00E5FF] transition-colors font-heading font-bold uppercase"
                        data-testid={`event-link-${event.id}`}>
                        <ExternalLink size={14} strokeWidth={2.5} /> Event Details
                      </a>
                    )}
                    {event.landing_enabled && !event.hide_homepage_link && (
                      <a href={`/event/${event.id}`}
                        className="flex items-center justify-center gap-2 w-full mt-2 bg-[#FF3366] text-white border-2 border-black px-4 py-3 font-heading font-bold text-xs uppercase tracking-wider hover:bg-[#e62e5c] shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] transition-all"
                        data-testid={`event-landing-btn-${event.id}`}>
                        <Flame size={14} strokeWidth={2.5} /> Order Now
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Book Us CTA */}
          <div className="border-2 border-black bg-black text-white p-8 md:p-10 shadow-[6px_6px_0px_0px_rgba(255,51,102,1)] text-center" data-testid="book-event-cta">
            <CalendarCheck size={36} strokeWidth={2} className="mx-auto mb-4 text-[#FF3366]" />
            <h3 className="font-heading text-2xl font-black uppercase mb-3">Book Us for Your Event</h3>
            <p className="text-zinc-300 text-sm max-w-lg mx-auto mb-6">Want live laser engraving at your event, market, or private gathering? We bring the setup, you bring the crowd.</p>
            <a
              href="mailto:boslaserengraving@gmail.com?subject=Event%20Booking%20Request&body=Hi%20Burn%20On%20Site%2C%0A%0AI%E2%80%99d%20like%20to%20book%20you%20for%20an%20event.%0A%0AEvent%20name%3A%0ADate%3A%0ALocation%3A%0AExpected%20attendees%3A%0A%0AThanks!"
              className="inline-flex items-center gap-3 bg-[#FF3366] text-white border-2 border-white px-8 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:bg-[#e62e5c] hover:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] transition-all"
              data-testid="book-event-btn"
            >
              <Mail size={18} strokeWidth={2.5} /> Request a Booking
            </a>
            <p className="text-zinc-500 text-xs font-mono mt-4">boslaserengraving@gmail.com</p>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="py-16 md:py-20 border-b-2 border-black bg-zinc-50" data-testid="gallery-section">
          <div className="max-w-7xl mx-auto px-8 md:px-16">
            <div className="text-center mb-10">
              <span className="font-mono text-xs font-bold tracking-[0.2em] text-[#FF3366] uppercase block mb-3">Our Work</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase">Completed Works</h2>
            </div>
            <GalleryCarousel images={galleryImages} speed={gallerySpeed} />
          </div>
        </section>
      )}

      {/* Video Gallery Section */}
      {galleryVideos.length > 0 && (
        <section className="py-16 md:py-20 border-b-2 border-black bg-white" data-testid="video-gallery-section">
          <div className="max-w-7xl mx-auto px-8 md:px-16">
            <div className="text-center mb-10">
              <span className="font-mono text-xs font-bold tracking-[0.2em] text-[#FF3366] uppercase block mb-3">In Action</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase">Video Gallery</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryVideos.map(video => (
                <div key={video.id} className="border-2 border-black bg-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] overflow-hidden" data-testid={`landing-video-${video.id}`}>
                  <video
                    src={`${API}/files/${video.storage_path}`}
                    className="w-full h-56 object-contain"
                    controls
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <div className="bg-white border-t-2 border-black px-4 py-2">
                    <span className="font-heading font-bold text-xs uppercase truncate block">{video.name.replace(/\.[^/.]+$/, "")}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Products Section */}
      <section id="products" className="px-8 md:px-16 py-16 md:py-24" data-testid="products-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <span className="font-mono text-xs font-bold tracking-[0.2em] text-[#FF3366] uppercase block mb-3">Our Collection</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase">Choose Your Product</h2>
            {productCategories.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6" data-testid="category-filter">
                <button onClick={() => setSelectedCategory("all")}
                  className={`px-4 py-2 border-2 border-black font-heading font-bold text-xs uppercase tracking-wider transition-all ${selectedCategory === "all" ? "bg-[#FF3366] text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"}`}
                  data-testid="category-all">
                  All
                </button>
                {productCategories.map(cat => (
                  <button key={cat} onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 border-2 border-black font-heading font-bold text-xs uppercase tracking-wider transition-all ${selectedCategory === cat ? "bg-[#FF3366] text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"}`}
                    data-testid={`category-${cat.replace(/\s/g,"-").toLowerCase()}`}>
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="brutal-card animate-pulse h-80" />
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="brutal-card p-12 text-center" data-testid="no-products-message">
              <Flame size={48} className="text-[#FF3366] mx-auto mb-4" strokeWidth={2.5} />
              <h3 className="font-heading text-xl font-bold mb-2">{selectedCategory !== "all" ? "No Products in This Category" : "No Products Yet"}</h3>
              <p className="text-zinc-500">{selectedCategory !== "all" ? "Try selecting a different category." : "Check back soon! Products are being added."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
              {filteredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} onClick={() => handleProductClick(product.id)} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Track Order CTA */}
      <section className="px-8 md:px-16 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="border-2 border-black p-6 flex items-center justify-between bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="track-order-cta">
            <div>
              <h3 className="font-heading font-bold text-sm uppercase">Already placed an order?</h3>
              <p className="text-xs text-zinc-500 mt-1">Check your order status at any time.</p>
            </div>
            <button onClick={() => navigate("/track")} className="brutal-btn brutal-btn-secondary text-sm flex items-center gap-2" data-testid="track-order-section-btn">
              <Search size={14} strokeWidth={2.5} /> Track Your Order
            </button>
          </div>
        </div>
      </section>

      {/* Floating Cart Button */}
      {cartCount > 0 && (
        <button onClick={() => navigate("/cart")} className="fixed bottom-6 right-6 z-50 bg-[#FF3366] text-white border-2 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all flex items-center gap-2" data-testid="floating-cart-btn">
          <ShoppingCart size={20} strokeWidth={2.5} />
          <span className="font-heading font-bold text-sm">{cartCount}</span>
        </button>
      )}

      {/* Contact Us Section */}
      {(contactInfo.contact_phone || contactInfo.contact_email || contactInfo.contact_location) && (
        <section className="px-8 md:px-16 pb-16" data-testid="contact-section">
          <div className="max-w-7xl mx-auto">
            <div className="border-2 border-black bg-black text-white p-8 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
              <span className="font-mono text-xs font-bold tracking-[0.2em] text-[#FF3366] uppercase block mb-2">Get In Touch</span>
              <h2 className="font-heading text-2xl sm:text-3xl font-black uppercase mb-6">Contact Us</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {contactInfo.contact_phone && (
                  <a href={`tel:${contactInfo.contact_phone}`} className="flex items-center gap-3 hover:text-[#00E5FF] transition-colors group" data-testid="contact-phone">
                    <div className="w-10 h-10 border-2 border-white/30 group-hover:border-[#00E5FF] flex items-center justify-center flex-shrink-0 transition-colors">
                      <Phone size={18} strokeWidth={2.5} />
                    </div>
                    <div>
                      <span className="font-heading font-bold text-xs uppercase tracking-wider text-zinc-400 block">Phone</span>
                      <span className="text-sm font-bold">{contactInfo.contact_phone}</span>
                    </div>
                  </a>
                )}
                {contactInfo.contact_email && (
                  <a href={`mailto:${contactInfo.contact_email}`} className="flex items-center gap-3 hover:text-[#00E5FF] transition-colors group" data-testid="contact-email">
                    <div className="w-10 h-10 border-2 border-white/30 group-hover:border-[#00E5FF] flex items-center justify-center flex-shrink-0 transition-colors">
                      <Mail size={18} strokeWidth={2.5} />
                    </div>
                    <div>
                      <span className="font-heading font-bold text-xs uppercase tracking-wider text-zinc-400 block">Email</span>
                      <span className="text-sm font-bold">{contactInfo.contact_email}</span>
                    </div>
                  </a>
                )}
                {contactInfo.contact_location && (
                  <div className="flex items-center gap-3" data-testid="contact-location">
                    <div className="w-10 h-10 border-2 border-white/30 flex items-center justify-center flex-shrink-0">
                      <MapPin size={18} strokeWidth={2.5} />
                    </div>
                    <div>
                      <span className="font-heading font-bold text-xs uppercase tracking-wider text-zinc-400 block">Location</span>
                      <span className="text-sm font-bold">{contactInfo.contact_location}</span>
                    </div>
                  </div>
                )}
              </div>
              {(contactInfo.contact_website || contactInfo.contact_social) && (
                <div className="mt-6 pt-4 border-t border-zinc-700 flex flex-wrap gap-4">
                  {contactInfo.contact_website && (
                    <a href={contactInfo.contact_website} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-zinc-400 hover:text-[#00E5FF] underline transition-colors" data-testid="contact-website">{contactInfo.contact_website}</a>
                  )}
                  {contactInfo.contact_social && (
                    <span className="text-xs font-mono text-zinc-400" data-testid="contact-social">{contactInfo.contact_social}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-black text-white border-t-2 border-black px-8 md:px-16 py-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8">
          <div>
            <h3 className="font-heading text-xl font-black text-[#FF3366] uppercase mb-2">Burn On Site</h3>
            <p className="text-zinc-400 text-sm max-w-xs">Custom laser engravings at live events. Your design, burned to perfection.</p>
          </div>
          <div className="flex flex-col items-end gap-4">
            <button
              onClick={() => navigate("/track")}
              className="flex items-center gap-2 text-zinc-400 hover:text-[#00E5FF] transition-colors text-xs font-mono uppercase tracking-wider"
              data-testid="track-order-footer-btn"
            >
              <Search size={12} strokeWidth={2.5} /> Track Order
            </button>
            <div className="font-mono text-xs text-zinc-500 tracking-wider uppercase">
              &copy; {new Date().getFullYear()} Burn On Site Laser Engraving
            </div>
            <button
              onClick={() => { setAdminDialogOpen(true); setAdminPassword(""); setAdminError(""); }}
              className="flex items-center gap-2 text-zinc-600 hover:text-[#FF3366] transition-colors text-xs font-mono uppercase tracking-wider"
              data-testid="admin-login-btn"
            >
              <Lock size={12} strokeWidth={2.5} /> Admin
            </button>
          </div>
        </div>
      </footer>

      {/* Admin Password Dialog */}
      <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-sm" data-testid="admin-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-lg uppercase flex items-center gap-2">
              <Lock size={18} strokeWidth={2.5} /> Admin Access
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Password</label>
              <input
                type="password"
                className="brutal-input"
                placeholder="Enter admin password"
                value={adminPassword}
                onChange={e => { setAdminPassword(e.target.value); setAdminError(""); }}
                onKeyDown={handleAdminKeyDown}
                autoFocus
                data-testid="admin-password-input"
              />
              {adminError && (
                <p className="text-[#FF3366] text-xs font-bold mt-2" data-testid="admin-password-error">{adminError}</p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => setAdminDialogOpen(false)} className="brutal-btn brutal-btn-outline text-xs px-4 py-2" data-testid="cancel-admin-btn">Cancel</button>
            <button
              onClick={handleAdminLogin}
              disabled={!adminPassword}
              className={`brutal-btn text-xs px-4 py-2 ${!adminPassword ? "opacity-50" : ""}`}
              data-testid="submit-admin-btn"
            >
              <Lock size={14} className="inline mr-1" strokeWidth={2.5} /> Enter
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
